/**
 * Where the session lives.
 *
 * THE ACCESS TOKEN IS HELD IN MEMORY ONLY
 * ---------------------------------------
 * It is never written to storage, so a successful XSS cannot read it out of
 * `localStorage` — it would have to exfiltrate on every page load instead of
 * once. The cost is that a refresh of the browser tab loses it, which is why
 * the refresh token exists and why the app boots by exchanging one.
 *
 * THE REFRESH TOKEN IS THE COMPROMISE, AND IT IS RECORDED AS ONE
 * --------------------------------------------------------------
 * It sits in `localStorage`. That is genuinely weaker than an httpOnly cookie,
 * which a script cannot read at all — but the gateway does not set one today
 * (open decision 2 in plans/xpsims-frontend-plan.md). `credentials: "include"`
 * is already set on every request in client.ts, so the day the gateway starts
 * issuing the cookie, this file drops its localStorage half and nothing else
 * changes.
 *
 * Until then the exposure is bounded deliberately: refresh tokens rotate on
 * use, so a stolen one is single-use and its use invalidates the real user's
 * session — which turns a silent theft into a visible logout.
 */

const REFRESH_KEY = "xpsims.refresh";

/** Called when the session changes, so React can re-render on it. */
type Listener = () => void;

let accessToken: string | null = null;
const listeners = new Set<Listener>();

/**
 * Reads the refresh token, tolerating storage being unavailable.
 *
 * Private browsing and locked-down enterprise profiles throw on access rather
 * than returning null, and an exception here would take down the whole app at
 * boot for a user who could otherwise simply log in again.
 *
 * @returns The stored refresh token, or null
 */
function readRefresh(): string | null {
    try {
        return window.localStorage.getItem(REFRESH_KEY);
    } catch {
        return null;
    }
}

/**
 * Writes or clears the refresh token, tolerating storage being unavailable.
 *
 * @param value - The token, or null to remove it
 */
function writeRefresh(value: string | null): void {
    try {
        if (value === null) {
            window.localStorage.removeItem(REFRESH_KEY);
        } else {
            window.localStorage.setItem(REFRESH_KEY, value);
        }
    } catch {
        // The session survives in memory for this tab; it just will not
        // outlive a reload. Failing the login over it would be worse.
    }
}

function notify(): void {
    for (const listener of listeners) {
        listener();
    }
}

export const tokenStore = {
    getAccessToken: (): string | null => accessToken,

    getRefreshToken: readRefresh,

    /**
     * Records a fresh pair after login or refresh.
     *
     * @param access - The new access token
     * @param refresh - The new refresh token
     */
    set(access: string, refresh: string): void {
        accessToken = access;
        writeRefresh(refresh);
        notify();
    },

    /** Ends the session. Idempotent — logout and a failed refresh both call it. */
    clear(): void {
        accessToken = null;
        writeRefresh(null);
        notify();
    },

    /**
     * Whether a session might be recoverable.
     *
     * Deliberately not "is the user logged in": a refresh token that the server
     * has since revoked looks identical from here. It answers whether booting
     * is worth attempting, and the server decides the rest.
     */
    hasSession: (): boolean => accessToken !== null || readRefresh() !== null,

    /**
     * Subscribes to session changes.
     *
     * @param listener - Called on every set or clear
     * @returns The unsubscribe function
     */
    subscribe(listener: Listener): () => void {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
};
